import React from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import Model from "@tripian/model";
// Trip detayın kullandığı güncel Viator bileşeni; eski ViatorTourInfo/ klasörü
// artık çağrılmıyor ama referans olarak duruyor.
import VistorTourInfo from "./VistorTourInfo/VistorTourInfo";
import GygTourInfo from "./GygTourInfo/GygTourInfo";
import BbTourInfo from "./BbTourInfo/BbTourInfo";
import useTranslate from "../../hooks/useTranslate";
import RezdyTourInfo from "./RezdyTourInfo/RezdyTourInfo";
import ToristyTourInfo from "./ToristyTourInfo/ToristyTourInfo";
import VictoryProductInfo from "./VictoryProductInfo/VictoryProductInfo";
import LivnTourInfo from "./LivnTourInfo/LivnTourInfo";
import AppNav from "../../App/AppNav/AppNav";
import { TOUR_INFO } from "../../constants/ROUTER_PATH_TITLE";

// http://localhost:3000/tour/4/407440?start_date=2023-12-29&end_date=2023-01-15
// http://localhost:3000/tour/4/10505?start_date=2023-12-29&end_date=2023-01-15

const TourInfoPage = () => {
  // /:providerId/:id?start_date=:date&end_date=:date&adults=:adult&children=:children
  //  YELP = 2,
  //  GYG = 4,
  //  OPEN_TABLE = 5,
  //  BOOK_BARBADOS = 6,
  //  UBER = 7

  const { providerId, id } = useParams<{
    providerId: string;
    id: string;
  }>();

  const location = useLocation();
  const { from, flags } = location.state || {};

  const useQuery = <T extends { [K in keyof T]?: string } = {}>(): T => {
    const query = new URLSearchParams(location.search);
    const result: T = {} as T;
    query.forEach((val, key) => {
      result[key] = val;
    });

    return result;
  };
  const {
    city_id,
    city_name,
    lat,
    lng,
    start_date,
    end_date,
    adults,
    children,
  } = useQuery<{
    city_id: string | undefined;
    city_name: string | undefined;
    lat: string | undefined;
    lng: string | undefined;
    start_date: string | undefined;
    end_date: string | undefined;
    adults: string | undefined;
    children: string | undefined;
  }>();

  const { t } = useTranslate();
  const navigate = useNavigate();

  /**
   * Bu provider bileşenleri trip detayda modal olarak kullanılıyor ve içerikleri
   * `isOpen`'a bağlı (GygTourInfo/ToristyTourInfo isOpen=false iken null döner,
   * Livn/Victory ise veriyi hiç çekmez). Rota olarak açıldıklarında da aynı
   * tasarımı göstermeleri için burada açık modda render ediliyorlar; kapatma
   * geçmişte bir geri gider, yani listeye döner.
   */
  const closeToPreviousPage = () => navigate(-1);

  /**
   * Modal tabanlı provider detaylarını rota bağlamına yerleştirir: navbar üstte
   * görünür kalır, panel de onun altından başlar (bkz. TourInfo.module.scss).
   * BbTourInfo ve RezdyTourInfo kendi AppNav'ını render ettiği için bu sarmalayıcıyı
   * kullanmaz.
   */
  const withNavbar = (content: React.ReactNode) => (
    <>
      <AppNav header={TOUR_INFO.HEADER?.(t("tourInfo.header"))} />
      <div className="tour-info-route">{content}</div>
    </>
  );

  if (!id) return null;

  if (Number(providerId) === Model.PROVIDER_ID.GYG)
    return withNavbar(
      <GygTourInfo
        cityId={Number(city_id)}
        cityName={city_name}
        tourId={Number(id)}
        adults={Number(adults)}
        startDate={start_date}
        endDate={end_date}
        children={Number(children)}
        isOpen
        onClose={closeToPreviousPage}
        t={t}
      />
    );

  if (Number(providerId) === Model.PROVIDER_ID.VIATOR)
    return withNavbar(
      <VistorTourInfo
        tourId={id}
        cityId={Number(city_id)}
        cityName={city_name}
        lat={Number(lat)}
        lng={Number(lng)}
        adults={Number(adults)}
        startDate={start_date}
        endDate={end_date}
        children={Number(children)}
        previousRoute={from}
        flags={flags}
        isOpen
        onClose={closeToPreviousPage}
      />
    );

  if (Number(providerId) === Model.PROVIDER_ID.BOOK_BARBADOS)
    return (
      <BbTourInfo
        tourId={Number(id)}
        startDate={start_date}
        endDate={end_date}
        adults={adults}
        children={children}
        t={t}
      />
    );

  if (Number(providerId) === Model.PROVIDER_ID.REZDY)
    return <RezdyTourInfo cityId={Number(city_id)} tourId={id} t={t} />;

  if (Number(providerId) === Model.PROVIDER_ID.TORISTY)
    return withNavbar(
      <ToristyTourInfo tourId={id} isOpen onClose={closeToPreviousPage} />
    );

  if (Number(providerId) === Model.PROVIDER_ID.VICTORY)
    return withNavbar(
      <VictoryProductInfo productId={id} isOpen onClose={closeToPreviousPage} />
    );

  if (Number(providerId) === Model.PROVIDER_ID.LIVN)
    return withNavbar(
      <LivnTourInfo
        tourId={Number(id)}
        startDate={start_date}
        endDate={end_date}
        isOpen
        onClose={closeToPreviousPage}
      />
    );

  return null;
};

export default TourInfoPage;
